import type {
  BaseContactMetier,
  ContactMetier,
  Poste,
  PosteSansAgent,
} from "models/ContactsMetiers";
import { cometApiClient } from "./apiClients";
import { Endpoints } from "constants/endpoints";

interface PageableDto<T> {
  values: T;
  continuationToken?: string;
}

/**
 * Interface du modèle de Contact Métier de l'API, le `poste` est une unique valeur,
 * la propriété `postes` n'existe que dans Collectivités
 */
interface ApiContactMetier extends Omit<BaseContactMetier, "postes"> {
  poste: Poste | PosteSansAgent;
}

export interface ContactMetierFilters {
  communesId: Array<string>;
  cadsId: Array<string>;
  perimetresId: Array<string>;
  territoiresId: Array<string>;
  competences?: Array<string>;
  metiersCode?: Array<string>;
  postesId?: Array<string>;
}

/**
 * Récupère les contacts métiers correspondant à plusieurs filtres, rangés par ordre alphabétique de métier
 * @returns PageableDto<Array<ContactMetierTerritoire>
 */
async function getByFilter<T extends BaseContactMetier = ContactMetier>(
  filters: Partial<ContactMetierFilters> = {},
  continuationToken?: string
): Promise<PageableDto<Array<T>>> {
  const {
    communesId = [],
    cadsId = [],
    perimetresId = [],
    territoiresId = [],
    competences = [],
    metiersCode = [],
    postesId = [],
  } = filters;

  const params = new URLSearchParams();
  if (continuationToken != null && continuationToken !== "") {
    params.append("continuationToken", continuationToken);
  }
  if (communesId.length > 0) params.append("communesId", communesId.join(","));
  if (perimetresId.length > 0) params.append("perimetresId", perimetresId.join(","));
  if (territoiresId.length > 0) params.append("territoiresId", territoiresId.join(","));
  if (cadsId.length > 0) params.append("communesAssocieesDelegueesId", cadsId.join(","));
  if (competences.length > 0) params.append("competences", competences.join(","));
  if (metiersCode.length > 0) params.append("metiersCode", metiersCode.join(","));
  if (postesId.length > 0) params.append("postesId", postesId.join(","));
  const queryString = params.size > 0 ? `?${params.toString()}` : "";

  const url = `${Endpoints.CONTACTS_METIERS}${queryString}`;
  const { values: apiValues, ...reste } = await cometApiClient.get<
    PageableDto<Array<ApiContactMetier>>,
    PageableDto<Array<ApiContactMetier>>
  >(url);

  const values = apiValues
    .map<T>(transformApiContactMetierToInternalContactMetier)
    .toSorted((cm1, cm2) => cm1.metier.libelle.localeCompare(cm2.metier.libelle));

  return { ...reste, values };
}

/**
 * Transforme un contact métier de l'API en contact métier de l'application Collectivité.
 * L'API retournant une propriété `poste` avec une unique valeur, il faut adapter l'objet
 * en renseignant la propriété `postes` (spécifique à Collectivé) avec un tableau de cette valeur.
 * @param apiContactMetier Modèle de Contact Métier selon l'API Comet
 * @returns Un contact métier transformé selon le modèle Contact Métier de Collectivité
 */
function transformApiContactMetierToInternalContactMetier<
  T extends BaseContactMetier = ContactMetier,
>(apiContactMetier: ApiContactMetier): T {
  const { poste, ...contact } = apiContactMetier;
  const internalContact = contact as T;
  internalContact.postes = [poste];
  return internalContact;
}

export const contactsMetiersService = {
  getByFilter,
};
